import React, { useState, useEffect, useMemo, useCallback } from "react";
import { PlusCircle, Trash } from "react-bootstrap-icons";
import PropTypes from "prop-types";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import FormInput from "Common/Custom/FormInput";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import FormTextarea from "Common/Custom/FormTextarea";
import FormSelect from 'Common/Custom/FormSelect';
import FormDatePicker from "Common/Custom/ReactFlatpickr";
import FormNumberInput from "Common/Custom/FormNumberInput";
import FormMultiSelect from "../../Componates/FormMultiSelect/FormMultiSelect";
import { getSelectOptions } from "utils/optionHelper";

const ReceiveLaundryForm = ({
  values,
  errors,
  handleChange,
  handleSubmit,
  loading,
  fields,
  item,
  ProductOptions,
  EmployeesOptions,
  TaskOptions,
  setValues,
  validateItems,
  setErrors
}) => {

  const optionConfig = {
    product_id: {
      list: ProductOptions,
      valueKey: "product_id",
      labelKey: "product_name"
    },
    operated_by: {
      list: EmployeesOptions,
      valueKey: "employee_id",
      labelKey: "first_name"
    }
  };

  const [items, setItems] = useState([]);

  // Calculate total cost for a row
  const calculateTotal = useCallback((row) => {
    const quantity = Number(row.quantity || 0);
    if (!quantity || !row.product_id || !Array.isArray(row.task_id) || row.task_id.length === 0) {
      return 0;
    }

    const taskTotal = row.task_id.reduce((sum, taskId) => {
      const task = TaskOptions.find(
        t =>
          Number(t.product_id) === Number(row.product_id) &&
          Number(t.task_id) === Number(taskId)
      );

      return sum + Number(task?.price || 0);
    }, 0);

    return taskTotal * quantity;
  }, [TaskOptions]);

  useEffect(() => {
    if (Array.isArray(values.items) && values.items.length > 0) {
      const mappedItems = values.items.map(item => ({
        product_id: Number(item.product_id),
        task_id: item.task_id,
        quantity: item.quantity,
        totle: calculateTotal({
          ...item,
          product_id: Number(item.product_id),
          task_id: item.task_id,
          quantity: item.quantity
        })
      }));
      setItems(mappedItems);
    }
  }, [values.items, calculateTotal]);

  useEffect(() => {
    if (items.length === 0 && !values.items) {
      setItems([{ product_id: "", task_id: [], quantity: "", totle: 0 }]);
    }
  }, [items, values.items]);

  // Get product options excluding already selected products in other rows
  const getAvailableProductOptions = useCallback((currentRowIndex) => {
    const selectedProductIds = items
      .map((row, idx) => idx !== currentRowIndex ? row.product_id : null)
      .filter(Boolean);

    return ProductOptions.filter(
      product => !selectedProductIds.includes(product.product_id) ||
        product.product_id === items[currentRowIndex].product_id
    );
  }, [items, ProductOptions]);

  // Handle row field changes
  const isValidTaskForProduct = (productId, taskId) => {
    return TaskOptions.some(
      t =>
        Number(t.product_id) === Number(productId) &&
        Number(t.task_id) === Number(taskId)
    );
  };

  const handleItemChange = useCallback((index, name, value) => {
    setItems(prevItems => {
      const updatedItems = prevItems.map((row, i) => {
        if (i !== index) return row;

        let updatedRow = { ...row, [name]: value };

        if (name === "product_id") {
          updatedRow.task_id = (row.task_id || []).map(Number).filter(taskId =>
            isValidTaskForProduct(value, taskId)
          );
        }

        if (name === "task_id") {
          updatedRow.task_id = (value || []).map(v => Number(v?.value ?? v)).filter(taskId =>
            isValidTaskForProduct(row.product_id, taskId)
          );
        }

        return {
          ...updatedRow,
          totle: calculateTotal(updatedRow) // ✅ recalc totle immediately
        };
      });

      setValues(prev => ({ ...prev, items: updatedItems }));
      const itemErrors = validateItems(updatedItems);
      setErrors(prev => ({ ...prev, items: itemErrors || [] }));

      return updatedItems;
    });
  }, [calculateTotal, validateItems, setErrors, setValues, isValidTaskForProduct]);

  // Add a new row
  const addRow = useCallback(() => {
    setItems([...items, { product_id: "", task_id: [], quantity: "", totle: 0 }]);
  }, [items]);

  // Delete a row
  const deleteRow = useCallback((index) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  }, [items]);

  // Memoized grand total calculation
  const grandTotal = useMemo(() => {
    return items.reduce((sum, item) => sum + Number(item.totle || 0), 0);
  }, [items]);

  // Render main form fields
  const renderFields = (fields) => {
    return fields.map((field) => {
      switch (field.type) {
        case "select":
          return (
            <FormSelect
              key={field.name}
              field={field}
              value={values[field.name]}
              handleChange={handleChange}
              options={getSelectOptions(field.name, optionConfig)}
              error={errors[field.name]}
            />
          );
        case "textarea":
          return (
            <FormTextarea
              key={field.name}
              field={field}
              value={values[field.name]}
              handleChange={handleChange}
              error={errors[field.name]}
            />
          );
        case "date":
          return (
            <FormDatePicker
              key={field.name}
              field={field}
              value={values[field.name]}
              handleChange={handleChange}
              error={errors[field.name]}
              mode="datetime"
            />
          );
        case "number":
          return (
            <FormNumberInput
              key={field.name}
              field={field}
              value={values[field.name]}
              handleChange={handleChange}
              error={errors[field.name]}
            />
          );
        default:
          return (
            <FormInput
              key={field.name}
              field={field}
              value={values[field.name]}
              handleChange={handleChange}
              error={errors[field.name]}
            />
          );
      }
    });
  };

  // Render rows of items
  const renderItemRows = () =>
    items.map((row, rowIndex) => (
      <tr key={rowIndex}>
        {item.map((field) => (
          <td key={field.name}>

            {/* PRODUCT */}
            {field.name === "product_id" && (
              <FormSelect
                field={field}
                value={row.product_id}
                options={getAvailableProductOptions(rowIndex).map(p => ({
                  value: p.product_id,
                  label: p.product_name
                }))}
                handleChange={(e) => handleItemChange(rowIndex, "product_id", e.target.value)}
                error={errors.items?.[rowIndex]?.product_id}
              />
            )}

            {/* TASK */}
            {field.name === "task_id" && (
              <FormMultiSelect
                field={field}
                disabled={!row.product_id}
                value={row.task_id || []}
                options={
                  TaskOptions
                    .filter(t => Number(t.product_id) === Number(row.product_id))
                    .map(t => ({
                      value: t.task_id,
                      label: `${t.task_name} ($${Math.round(Number(t.price))})`
                    }))
                }
                handleChange={(e) => handleItemChange(rowIndex, "task_id", e.target.value)}
                error={errors.items?.task_id}
              />
            )}

            {/* OTHER SELECTS */}
            {field.type === "select" &&
              field.name !== "product_id" &&
              field.name !== "task_id" && (
                <FormSelect
                  field={field}
                  disabled={!row.product_id && !row.task_id}
                  value={row[field.name] || ""}
                  options={getSelectOptions(field.name, optionConfig)}
                  handleChange={(e) => handleItemChange(rowIndex, field.name, e.target.value)}
                  error={errors.items?.[rowIndex]?.[field.name]}
                />
              )}

            {/* QUANTITY */}
            {field.type === "number" && field.name === "quantity" && (() => {
              
              return (
                <FormNumberInput
                  field={{
                    ...field,
                    placeholder: `available quantity `,
                    disabled: (!row.product_id || row.task_id?.length <= 0)
                  }}
                  value={row.quantity || ""}
                  handleChange={(e) => handleItemChange(rowIndex, "quantity", e.target.value)}
                  error={errors.items?.[rowIndex]?.quantity}
                />
              );
            })()}


            {/* TOTLE (read-only) */}
            {field.name === "totle" && (
              <FormNumberInput
                field={{ ...field, disabled: true }}
                value={row.totle || 0}
                error={errors.items?.[rowIndex]?.totle}
              />
            )}

          </td>
        ))}

        <td className="text-center">
          <button type="button"
            className="btn btn-outline-success btn-sm me-2"
            onClick={addRow}
            disabled={
              (getAvailableProductOptions(rowIndex)?.length <= 1) || (!row.product_id || row.task_id?.length <= 0 || !row.quantity)}
          >
            <PlusCircle size={16} />
          </button>
          <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => deleteRow(rowIndex)} disabled={items.length <= 1}>
            <Trash size={16} />
          </button>
        </td>
      </tr>
    ));

  return (
    <PageBody onSubmit={handleSubmit} headerchildren={<HeadingOne>Receive Laundry Item</HeadingOne>}>
      {renderFields(fields)}

      <div className="col-12 mt-4">
        <div className="table-responsive">
          <table className="table table-bordered align-middle">
            <thead>
              <tr>
                <th colSpan="6" className="text-center">Laundry Item</th>
              </tr>
              <tr>
                <th>Product</th>
                <th>Task</th>
                <th>Quantity</th>
                <th>Totle Cost</th>
                <th width="120">Action</th>
              </tr>
            </thead>
            <tbody>
              {renderItemRows()}
              <tr>
                <td colSpan={3} className="text-end">Grand Total</td>
                <td colSpan={2}>
                  {grandTotal}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <FormSubmitButton
        label="Receive"
        loading={loading}
        colSize="col-12"
        align="end"
      />
    </PageBody>
  );
};

ReceiveLaundryForm.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  fields: PropTypes.array.isRequired,
  item: PropTypes.array.isRequired,
  ProductOptions: PropTypes.array.isRequired,
  EmployeesOptions: PropTypes.array.isRequired,
  TaskOptions: PropTypes.array.isRequired,
  setValues: PropTypes.func.isRequired,
  validateItems: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
};

ReceiveLaundryForm.defaultProps = {
  errors: {},
  loading: false,
};

export default ReceiveLaundryForm;
